import React, { useState } from "react";
import { Navbar } from "@/components/Navbar";
import { Footer } from "@/components/Footer";
import { ChevronDown } from "lucide-react";

const FAQ_GROUPS = [
  {
    group: "Booking",
    items: [
      {
        q: "How do I book a vehicle on eBikeGo?",
        a: "Browse the fleet, pick a plan (hourly, daily, weekly or monthly), choose your pickup city and dates, and complete the payment. You'll get a confirmation instantly.",
      },
      {
        q: "Can I book on behalf of someone else?",
        a: "The vehicle can only be operated by the rider listed on the booking. Please book from the actual rider's account for a smooth handover.",
      },
      {
        q: "Do you offer doorstep delivery?",
        a: "Yes — doorstep delivery and pickup are available across every launch city. The delivery window is shared on your booking confirmation.",
      },
    ],
  },
  {
    group: "Documents",
    items: [
      {
        q: "What documents do I need at pickup?",
        a: "A valid government-issued photo ID. For high-speed scooters and electric motorcycles, a valid Indian driving licence is also mandatory.",
      },
      {
        q: "Do you accept international driving licences?",
        a: "Yes, for tourists we accept an International Driving Permit along with the original licence from your country.",
      },
    ],
  },
  {
    group: "Payments",
    items: [
      {
        q: "Which payment methods are supported?",
        a: "Cards, UPI and net-banking through our secure payment partners. We never store your card details.",
      },
      {
        q: "Is there a security deposit?",
        a: "For longer weekly and monthly plans a refundable deposit is charged and released within 5–7 business days after the vehicle is returned.",
      },
    ],
  },
  {
    group: "Rides & Safety",
    items: [
      {
        q: "Is a helmet included?",
        a: "Yes, one helmet is provided with every rental. Additional helmets can be requested at pickup.",
      },
      {
        q: "What if the vehicle breaks down?",
        a: "Call our 24×7 roadside assistance. We aim to reach you in under 45 minutes and swap the vehicle if repair on the spot isn't possible.",
      },
    ],
  },
];

const Item = ({ q, a, idx }) => {
  const [open, setOpen] = useState(false);
  return (
    <div className="border-b border-gray-200" data-testid={`faq-item-${idx}`}>
      <button
        onClick={() => setOpen((v) => !v)}
        data-testid={`faq-toggle-${idx}`}
        className="w-full flex items-center justify-between text-left py-5 group"
      >
        <span className="font-display font-bold text-base md:text-lg text-ink group-hover:text-[#2D7FF9] transition-colors">
          {q}
        </span>
        <ChevronDown
          size={20}
          className={`shrink-0 text-gray-400 transition-transform ${open ? "rotate-180 text-[#2D7FF9]" : ""}`}
        />
      </button>
      {open && (
        <p className="pb-6 text-gray-700 leading-relaxed text-[15px] pr-8">{a}</p>
      )}
    </div>
  );
};

const FAQs = () => {
  let idx = 0;
  return (
    <div className="min-h-screen bg-[#F8F9FA]" data-testid="faqs-page">
      <Navbar />
      <div className="bg-white border-b border-gray-100">
        <div className="max-w-7xl mx-auto px-5 md:px-8 py-14">
          <p className="text-[10px] uppercase tracking-widest text-[#2D7FF9] font-bold font-mono-num">— FAQs</p>
          <h1 className="font-display font-extrabold text-4xl md:text-5xl mt-3 tracking-tight text-ink">
            Questions, answered.
          </h1>
          <p className="text-gray-600 mt-4 max-w-2xl text-[15px] leading-relaxed">
            Everything you need to know about renting on eBikeGo. Can't find
            what you're looking for? Reach us on the Support page.
          </p>
        </div>
      </div>

      <div className="max-w-4xl mx-auto px-5 md:px-8 py-14 space-y-14">
        {FAQ_GROUPS.map((g) => (
          <section key={g.group}>
            <h2 className="font-display font-bold text-xl md:text-2xl text-ink mb-2 pb-3 border-b-2 border-[#3DDC97] inline-block">
              {g.group}
            </h2>
            <div className="mt-4">
              {g.items.map((it) => {
                const i = idx++;
                return <Item key={i} {...it} idx={i} />;
              })}
            </div>
          </section>
        ))}
      </div>

      <Footer />
    </div>
  );
};

export default FAQs;
